import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Palette, ExternalLink, Loader2 } from "lucide-react";

const inputCls =
  "w-full bg-ink border border-stroke px-3 py-2 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid";
const empty = { name: "", bio: "", style_tags: [], website: "" };

export default function ArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchArtists = () => api.get("/artists").then((r) => setArtists(r.data));
  useEffect(() => { fetchArtists(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setTags(""); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm(a); setTags((a.style_tags || []).join(", ")); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    const payload = { ...form, style_tags: tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/artists/${editing.id}`, payload);
      else await api.post("/artists", payload);
      toast.success("Saved");
      setOpen(false);
      fetchArtists();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete ${a.name}?`)) return;
    await api.delete(`/artists/${a.id}`);
    toast.success("Deleted");
    fetchArtists();
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Poster Illustrators</div>
          <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">ARTISTS</h1>
        </div>
        <button onClick={openAdd} data-testid="add-artist-button"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-5 py-3 hard-shadow">
          <Plus size={16} /> Add Artist
        </button>
      </div>

      {artists.length === 0 ? (
        <div className="border border-dashed border-stroke p-12 text-center font-mono text-sm text-muted-gray">
          Track your favourite poster artists — Drew Millward, Emek, and more.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {artists.map((a) => (
            <div key={a.id} className="bg-surface border border-stroke p-5 hard-shadow group" data-testid="artist-card">
              <div className="flex items-start justify-between">
                <Palette className="text-magenta" size={22} />
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(a)} className="text-muted-gray hover:text-acid"><Pencil size={15} /></button>
                  <button onClick={() => del(a)} className="text-muted-gray hover:text-magenta"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="font-display text-3xl text-paper mt-3 leading-none">{a.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-acid mt-1">{a.poster_count} in vault</div>
              {a.bio && <p className="font-sans text-sm text-muted-gray mt-3 leading-relaxed line-clamp-4">{a.bio}</p>}
              {a.style_tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {a.style_tags.map((t) => (
                    <span key={t} className="font-mono text-[10px] px-2 py-0.5 border border-stroke text-cyan">{t}</span>
                  ))}
                </div>
              )}
              {a.website && (
                <a href={a.website} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-gray hover:text-acid mt-3">
                  <ExternalLink size={12} /> Website
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/85 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg bg-surface border border-stroke my-8" data-testid="artist-form-dialog">
            <div className="flex items-center justify-between p-5 border-b border-stroke">
              <h2 className="font-display text-3xl text-paper">{editing ? "Edit Artist" : "Add Artist"}</h2>
              <button onClick={() => setOpen(false)} className="text-muted-gray hover:text-magenta"><X size={22} /></button>
            </div>
            <div className="p-5 space-y-4">
              <input data-testid="artist-name-input" className={inputCls} placeholder="Name (e.g. Drew Millward)"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <textarea className={`${inputCls} min-h-[100px] resize-y`} placeholder="Bio / style notes"
                value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
              <input className={inputCls} placeholder="Style tags (comma separated)"
                value={tags} onChange={(e) => setTags(e.target.value)} />
              <input className={inputCls} placeholder="Website URL"
                value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
            <div className="p-5 border-t border-stroke flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2.5 font-mono text-xs uppercase border border-stroke text-muted-gray hover:text-paper">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="artist-save-button"
                className="px-6 py-2.5 font-mono text-xs uppercase bg-acid text-ink font-bold hover:bg-glow flex items-center gap-2 disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
